import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import { useOutletContext } from "react-router-dom";

import { Modal } from "../../../components/dashboard/Modal";
import { useToast } from "../../../contexts/ToastContext";
import { useTheme } from "../../../contexts/ThemeContext";
import { extractErrorMessage } from "../../../lib/api";
import {
  approveUser,
  deleteUser,
  denyUser,
  getSettings,
  inviteUser,
  listUsers,
  resendInvite,
  saveSettings,
  testSmtp,
  updateUserRole,
} from "../../../lib/dashboardApi";
import type { InviteResult } from "../../../lib/dashboardApi";
import { hasMinRole } from "../../../lib/hooks/useAuthMe";
import { useConfirm } from "../../../contexts/ConfirmContext";
import type { Settings as SettingsType, User } from "../../../types/dashboard";

type SettingsCtx = { user?: User };

const TZ_OPTIONS: Array<[string, string]> = [
  ["America/New_York", "Eastern (America/New_York)"],
  ["America/Chicago", "Central (America/Chicago)"],
  ["America/Denver", "Mountain (America/Denver)"],
  ["America/Boise", "Mountain (America/Boise)"],
  ["America/Los_Angeles", "Pacific (America/Los_Angeles)"],
  ["America/Anchorage", "Alaska"],
  ["Pacific/Honolulu", "Hawaii"],
  ["UTC", "UTC"],
];

export function Settings() {
  const { user } = useOutletContext<SettingsCtx>();
  const { toast } = useToast();
  const { setTheme } = useTheme();
  const qc = useQueryClient();
  const confirm = useConfirm();
  const { data: settings, isLoading } = useQuery({ queryKey: ["settings"], queryFn: getSettings });
  const isOwner = user?.role === "owner";
  const isAdmin = hasMinRole(user, "admin");
  const { data: users = [] } = useQuery({
    queryKey: ["users"],
    queryFn: listUsers,
    enabled: isAdmin,
  });

  const [inviteOpen, setInviteOpen] = useState(false);
  const [inviteEmail, setInviteEmail] = useState("");
  const [inviteName, setInviteName] = useState("");
  const [inviteRole, setInviteRole] = useState("viewer");
  const [inviteError, setInviteError] = useState<string | null>(null);
  const [inviting, setInviting] = useState(false);

  const [form, setForm] = useState<SettingsType>({});
  useEffect(() => {
    if (settings) setForm(settings);
  }, [settings]);

  function patch<K extends keyof SettingsType>(key: K, value: SettingsType[K]) {
    setForm((f) => ({ ...f, [key]: value }));
  }

  async function save(group: "general" | "alerts" | "smtp" | "maintenance") {
    try {
      await saveSettings(form);
      qc.invalidateQueries({ queryKey: ["settings"] });
      toast(`Saved ${group} settings.`);
    } catch (e) {
      toast(extractErrorMessage(e, "Could not save settings."), "error");
    }
  }

  async function runSmtpTest() {
    const recipient = user?.email;
    if (!recipient) {
      toast("Your account has no email address to send the test to.", "error");
      return;
    }
    try {
      await testSmtp(recipient);
      toast(`Test email sent to ${recipient}. Check your inbox.`);
    } catch (e) {
      toast(extractErrorMessage(e, "SMTP test failed."), "error");
    }
  }

  async function handleApprove(u: User, role: string) {
    try {
      await approveUser(u.id, role);
      qc.invalidateQueries({ queryKey: ["users"] });
      toast(`Approved ${u.email}.`);
    } catch (e) {
      toast(extractErrorMessage(e, "Could not approve user."), "error");
    }
  }
  async function handleDeny(u: User) {
    if (!(await confirm({ title: "Deny request?", message: `Deny ${u.email}?`, danger: true })))
      return;
    try {
      await denyUser(u.id);
      qc.invalidateQueries({ queryKey: ["users"] });
      toast("User denied.");
    } catch (e) {
      toast(extractErrorMessage(e, "Could not deny user."), "error");
    }
  }
  async function handleRoleChange(u: User, role: string) {
    try {
      await updateUserRole(u.id, role);
      qc.invalidateQueries({ queryKey: ["users"] });
      toast("Role updated.");
    } catch (e) {
      toast(extractErrorMessage(e, "Could not update role."), "error");
    }
  }
  async function handleDelete(u: User) {
    if (
      !(await confirm({
        title: "Delete user?",
        message: `Permanently remove ${u.email}?`,
        danger: true,
      }))
    )
      return;
    try {
      await deleteUser(u.id);
      qc.invalidateQueries({ queryKey: ["users"] });
      toast("User deleted.");
    } catch (e) {
      toast(extractErrorMessage(e, "Could not delete user."), "error");
    }
  }

  function toastInviteResult(email: string, result: InviteResult) {
    if (result.email_sent) {
      toast(`Invite sent to ${email}.`);
    } else {
      toast(
        `Account created, but the invite email failed: ${result.email_error ?? "unknown error"}. ` +
          "Check SMTP settings, then use Resend.",
        "error",
      );
    }
  }

  async function handleInvite() {
    const email = inviteEmail.trim().toLowerCase();
    if (!email) {
      setInviteError("Email is required.");
      return;
    }
    setInviteError(null);
    setInviting(true);
    try {
      const result = await inviteUser(email, inviteRole, inviteName.trim());
      qc.invalidateQueries({ queryKey: ["users"] });
      toastInviteResult(email, result);
      setInviteOpen(false);
      setInviteEmail("");
      setInviteName("");
      setInviteRole("viewer");
    } catch (e) {
      setInviteError(extractErrorMessage(e, "Could not invite user."));
    } finally {
      setInviting(false);
    }
  }

  async function handleResendInvite(u: User) {
    try {
      const result = await resendInvite(u.id);
      toastInviteResult(u.email, result);
    } catch (e) {
      toast(extractErrorMessage(e, "Could not resend invite."), "error");
    }
  }

  const pending = users.filter((u) => u.status === "pending");
  const approved = users.filter((u) => u.status !== "pending");

  if (!hasMinRole(user, "admin")) {
    return <div className="empty">You don&apos;t have permission to view settings.</div>;
  }

  return (
    <>
      <h2 className="page-title">Settings</h2>
      <div className="settings-grid">
        {isAdmin && (
          <div className="card card-full">
            <div className="card-header">
              <h3>User Management</h3>
              <button
                type="button"
                className="btn btn-primary btn-sm"
                onClick={() => setInviteOpen(true)}
              >
                Invite User
              </button>
            </div>
            <div style={{ marginBottom: 20 }}>
              <h4
                style={{
                  fontSize: 13,
                  color: "var(--text2)",
                  textTransform: "uppercase",
                  letterSpacing: 0.5,
                  marginBottom: 10,
                }}
              >
                Pending Approval
              </h4>
              {pending.length === 0 ? (
                <div className="empty" style={{ padding: 16 }}>No pending requests.</div>
              ) : (
                <table>
                  <thead>
                    <tr>
                      <th>Name</th>
                      <th>Email</th>
                      <th>Requested</th>
                      <th>Email</th>
                      <th></th>
                    </tr>
                  </thead>
                  <tbody>
                    {pending.map((u) => (
                      <tr key={u.id}>
                        <td>{u.name}</td>
                        <td>{u.email}</td>
                        <td>{u.created_at?.slice(0, 10) ?? "-"}</td>
                        <td>
                          {u.email_verified ? (
                            <span className="badge badge-online">Verified</span>
                          ) : (
                            <span className="badge badge-warn">Unverified</span>
                          )}
                        </td>
                        <td>
                          {/* Admins and the owner can approve/deny requests. */}
                          {isAdmin && (
                            <div style={{ display: "flex", gap: 6 }}>
                              <select
                                className="form-input"
                                defaultValue="editor"
                                id={`role-${u.id}`}
                                style={{ width: 120, padding: 6 }}
                              >
                                <option value="viewer">Viewer</option>
                                <option value="editor">Editor</option>
                                <option value="admin">Admin</option>
                              </select>
                              <button
                                type="button"
                                className="btn btn-primary btn-sm"
                                onClick={() => {
                                  const sel = document.getElementById(
                                    `role-${u.id}`,
                                  ) as HTMLSelectElement;
                                  handleApprove(u, sel.value);
                                }}
                              >
                                Approve
                              </button>
                              <button
                                type="button"
                                className="btn btn-danger btn-sm"
                                onClick={() => handleDeny(u)}
                              >
                                Deny
                              </button>
                            </div>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
            <div>
              <h4
                style={{
                  fontSize: 13,
                  color: "var(--text2)",
                  textTransform: "uppercase",
                  letterSpacing: 0.5,
                  marginBottom: 10,
                }}
              >
                Approved Users
              </h4>
              <table>
                <thead>
                  <tr>
                    <th>Name</th>
                    <th>Email</th>
                    <th>Role</th>
                    <th>Joined</th>
                    <th></th>
                  </tr>
                </thead>
                <tbody>
                  {approved.map((u) => (
                    <tr key={u.id}>
                      <td>
                        {u.name || "—"}{" "}
                        {u.status === "invited" && (
                          <span className="badge badge-warn">Invited</span>
                        )}
                      </td>
                      <td>{u.email}</td>
                      <td>
                        {u.role === "owner" ? (
                          <span className="badge badge-online">Owner</span>
                        ) : isOwner ? (
                          <select
                            className="form-input"
                            value={u.role}
                            onChange={(e) => handleRoleChange(u, e.target.value)}
                            style={{ width: 120, padding: 6 }}
                          >
                            <option value="viewer">Viewer</option>
                            <option value="editor">Editor</option>
                            <option value="admin">Admin</option>
                          </select>
                        ) : (
                          u.role
                        )}
                      </td>
                      <td>{u.created_at?.slice(0, 10) ?? "-"}</td>
                      <td>
                        <div style={{ display: "flex", gap: 6 }}>
                          {u.status === "invited" && (
                            <button
                              type="button"
                              className="btn btn-outline btn-sm"
                              onClick={() => handleResendInvite(u)}
                            >
                              Resend Invite
                            </button>
                          )}
                          {isOwner && u.role !== "owner" && (
                            <button
                              type="button"
                              className="btn btn-danger btn-sm"
                              onClick={() => handleDelete(u)}
                            >
                              Remove
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        <div className="card">
          <div className="card-header">
            <h3>General</h3>
          </div>
          {isLoading ? (
            <div className="empty">Loading…</div>
          ) : (
            <>
              <div className="form-group">
                <label>Company Name</label>
                <input
                  className="form-input"
                  value={form.company_name ?? ""}
                  onChange={(e) => patch("company_name", e.target.value)}
                />
              </div>
              <div className="form-group">
                <label>Theme</label>
                <select
                  className="form-input"
                  value={form.theme ?? "light"}
                  onChange={(e) => {
                    patch("theme", e.target.value);
                    setTheme(e.target.value as "light" | "dark");
                  }}
                >
                  <option value="light">Light</option>
                  <option value="dark">Dark</option>
                </select>
              </div>
              <div className="form-group">
                <label>Timezone</label>
                <select
                  className="form-input"
                  value={form.timezone ?? "America/New_York"}
                  onChange={(e) => patch("timezone", e.target.value)}
                >
                  {TZ_OPTIONS.map(([v, label]) => (
                    <option key={v} value={v}>
                      {label}
                    </option>
                  ))}
                </select>
              </div>
              <div className="form-group">
                <label>Session Timeout (minutes)</label>
                <input
                  type="number"
                  className="form-input"
                  min={5}
                  max={1440}
                  value={form.session_timeout_min ?? 60}
                  onChange={(e) => patch("session_timeout_min", Number(e.target.value))}
                  style={{ maxWidth: 120 }}
                />
              </div>
              <button type="button" className="btn btn-primary" onClick={() => save("general")}>
                Save General
              </button>
            </>
          )}
        </div>

        <div className="card">
          <div className="card-header">
            <h3>Alerts</h3>
          </div>
          <div className="form-group">
            <label>Alert Recipient Emails (comma-separated)</label>
            <input
              className="form-input"
              value={form.alert_emails ?? ""}
              onChange={(e) => patch("alert_emails", e.target.value)}
              placeholder="admin@company.com, ops@company.com"
            />
          </div>
          <div className="form-group">
            <label>Offline Threshold (hours before alert)</label>
            <input
              type="number"
              className="form-input"
              min={1}
              max={168}
              value={form.alert_offline_hours ?? 24}
              onChange={(e) => patch("alert_offline_hours", Number(e.target.value))}
              style={{ maxWidth: 120 }}
            />
          </div>
          {(
            [
              ["alert_on_error", "Alert on playback errors"],
              ["alert_on_back_online", "Alert when Pi comes back online"],
              ["alert_digest", "Daily digest mode (instead of immediate)"],
              ["alert_enabled", "Enable email alerts"],
            ] as const
          ).map(([key, label]) => (
            <label
              key={key}
              style={{
                display: "flex",
                alignItems: "center",
                gap: 8,
                fontSize: 13,
                color: "var(--text2)",
                cursor: "pointer",
                marginBottom: 8,
              }}
            >
              <input
                type="checkbox"
                checked={!!form[key]}
                onChange={(e) => patch(key, e.target.checked)}
              />
              {label}
            </label>
          ))}
          <button type="button" className="btn btn-primary" onClick={() => save("alerts")}>
            Save Alerts
          </button>
        </div>

        <div className="card card-full">
          <div className="card-header">
            <h3>SMTP Configuration</h3>
          </div>
          <p style={{ color: "var(--text2)", fontSize: 13, marginBottom: 16 }}>
            Configure SMTP for email alerts. For Gmail, use an App Password.
          </p>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "0 20px" }}>
            <div className="form-group">
              <label>SMTP Host</label>
              <input
                className="form-input"
                value={form.smtp_host ?? ""}
                onChange={(e) => patch("smtp_host", e.target.value)}
                placeholder="smtp.gmail.com"
              />
            </div>
            <div className="form-group">
              <label>SMTP Port</label>
              <input
                type="number"
                className="form-input"
                value={form.smtp_port ?? 587}
                onChange={(e) => patch("smtp_port", Number(e.target.value))}
                style={{ maxWidth: 120 }}
              />
            </div>
            <div className="form-group">
              <label>Username / Email</label>
              <input
                className="form-input"
                value={form.smtp_user ?? ""}
                onChange={(e) => patch("smtp_user", e.target.value)}
              />
            </div>
            <div className="form-group">
              <label>Password / App Password</label>
              <input
                type="password"
                className="form-input"
                value={form.smtp_password ?? ""}
                onChange={(e) => patch("smtp_password", e.target.value)}
              />
            </div>
            <div className="form-group">
              <label>From Address</label>
              <input
                className="form-input"
                value={form.smtp_from ?? ""}
                onChange={(e) => patch("smtp_from", e.target.value)}
                placeholder="noreply@yourcompany.com"
              />
            </div>
            <div className="form-group">
              <label
                style={{ display: "flex", alignItems: "center", gap: 8, marginTop: 24 }}
              >
                <input
                  type="checkbox"
                  checked={!!form.smtp_use_tls}
                  onChange={(e) => patch("smtp_use_tls", e.target.checked)}
                />
                Use TLS (STARTTLS)
              </label>
            </div>
          </div>
          <div style={{ display: "flex", gap: 12 }}>
            <button type="button" className="btn btn-primary" onClick={() => save("smtp")}>
              Save SMTP
            </button>
            <button type="button" className="btn btn-outline" onClick={runSmtpTest}>
              Send Test Email
            </button>
          </div>
        </div>

        <div className="card card-full">
          <div className="card-header">
            <h3>Maintenance</h3>
          </div>
          <div style={{ display: "flex", gap: 24, flexWrap: "wrap" }}>
            <div className="form-group">
              <label>Auto-purge playback logs older than (days, 0 = never)</label>
              <input
                type="number"
                className="form-input"
                min={0}
                max={3650}
                value={form.purge_logs_days ?? 90}
                onChange={(e) => patch("purge_logs_days", Number(e.target.value))}
                style={{ maxWidth: 120 }}
              />
            </div>
            <div className="form-group">
              <label>Auto-purge unused media older than (days, 0 = never)</label>
              <input
                type="number"
                className="form-input"
                min={0}
                max={3650}
                value={form.purge_media_days ?? 0}
                onChange={(e) => patch("purge_media_days", Number(e.target.value))}
                style={{ maxWidth: 120 }}
              />
            </div>
          </div>
          <button type="button" className="btn btn-primary" onClick={() => save("maintenance")}>
            Save Maintenance
          </button>
        </div>
      </div>

      {/* Kept outside .settings-grid: the .card fadeUp animation leaves a
          transform on the card, which would turn it into the containing block
          for this fixed-position backdrop and trap the modal inside the tile. */}
      <Modal open={inviteOpen} onClose={() => setInviteOpen(false)} ariaLabel="Invite user">
        <h3>Invite User</h3>
        <p style={{ color: "var(--text2)", fontSize: 13, marginBottom: 16 }}>
          They&apos;ll get an email with a link to choose a password and activate their account.
        </p>
        {inviteError && <div className="form-error">{inviteError}</div>}
        <div className="form-group">
          <label htmlFor="invite-email">Email</label>
          <input
            id="invite-email"
            className="form-input"
            type="email"
            placeholder="person@company.com"
            value={inviteEmail}
            onChange={(e) => setInviteEmail(e.target.value)}
          />
        </div>
        <div className="form-group">
          <label htmlFor="invite-name">Name (optional)</label>
          <input
            id="invite-name"
            className="form-input"
            type="text"
            placeholder="Their name"
            value={inviteName}
            onChange={(e) => setInviteName(e.target.value)}
          />
        </div>
        <div className="form-group">
          <label htmlFor="invite-role">Role</label>
          <select
            id="invite-role"
            className="form-input"
            value={inviteRole}
            onChange={(e) => setInviteRole(e.target.value)}
          >
            <option value="viewer">Viewer</option>
            <option value="editor">Editor</option>
            <option value="admin">Admin</option>
          </select>
        </div>
        <div className="modal-actions">
          <button type="button" className="btn btn-outline" onClick={() => setInviteOpen(false)}>
            Cancel
          </button>
          <button
            type="button"
            className="btn btn-primary"
            disabled={inviting}
            onClick={handleInvite}
          >
            {inviting ? "Sending…" : "Send Invite"}
          </button>
        </div>
      </Modal>
    </>
  );
}
